import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import { useDispatch, useSelector } from "react-redux";
import {
  useForm,
  type UseFormRegister,
  type FieldErrors,
} from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CustomButton,
  CustomTextInput,
  CustomModal,
  CustomLoading,
  FixedActionBar,
  colors,
} from "@ippon/shared-components";
import { store, type AppDispatch, type RootState } from "@/store";
import {
  fetchExpenseCustomFields,
  createExpenseCustomField,
  updateExpenseCustomField,
  deleteExpenseCustomField,
  type ExpenseCustomFieldRecord,
  type ExpenseCustomFieldType,
} from "@/store/expenseCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";

/**
 * 支出 (Expense) は section_key を持たない単一フォーム。
 * 物件構成 / 点検-清掃と見た目を揃えるため、単一アコーディオン
 * 「支出フォーム」で全カスタム項目を管理する。
 */
interface SectionDef {
  key: "expense";
  name: string;
  fixedFields: { id: string; label: string; required: boolean }[];
}

// 並び・ラベルは実フォーム (pages/Expenses/Detail/index.tsx) に合わせている。
// 順序を変える時は実フォーム側も同時に更新すること。
const SECTIONS: SectionDef[] = [
  {
    key: "expense",
    name: "支出情報",
    fixedFields: [
      { id: "name", label: "支出タイトル", required: true },
      { id: "target_type", label: "場所区分", required: false },
      { id: "target_id", label: "名称", required: false },
      { id: "category", label: "カテゴリ", required: true },
      { id: "linked_repair_id", label: "工事名 (カテゴリを工事費に選択した場合)", required: false },
      { id: "status", label: "ステータス", required: false },
      { id: "expense_type", label: "経費区分", required: true },
      { id: "budget", label: "予算", required: false },
      { id: "amount", label: "支払金額", required: true },
      { id: "date", label: "発生日", required: true },
      { id: "payee", label: "支払先", required: false },
      { id: "tax_rate", label: "税率", required: false },
      { id: "tax_amount", label: "消費税額", required: false },
      { id: "partner_name", label: "取引先名", required: false },
      { id: "registration_number", label: "登録番号", required: false },
      { id: "description", label: "備考", required: false },
      { id: "documents", label: "その他関連書類", required: false },
    ],
  },
];

const ACCORDION_SX = {
  border: "1px solid #d9d9d9",
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { borderRadius: "10px" },
} as const;

const ACCORDION_SUMMARY_SX = {
  px: 2,
  py: 0,
  minHeight: 50,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "7px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 50 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "7px 0" },
} as const;

interface DraftField {
  field_id: string;
  label: string;
  field_type: ExpenseCustomFieldType;
  options: string;
  dirty: boolean;
  isNew?: boolean;
}

const fieldToDraft = (f: ExpenseCustomFieldRecord): DraftField => ({
  field_id: f.field_id,
  label: f.label,
  field_type: f.field_type,
  options: (f.options || []).join("\n"),
  dirty: false,
});

const normalizeDraftOptions = (options: string) =>
  options
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");

const isDraftDirtyFromRecord = (
  draft: DraftField,
  record?: ExpenseCustomFieldRecord,
) => {
  if (draft.isNew) return draft.label.trim().length > 0;
  if (!record) return draft.dirty;
  return (
    draft.label !== record.label ||
    draft.field_type !== record.field_type ||
    normalizeDraftOptions(draft.options) !==
      normalizeDraftOptions((record.options || []).join("\n"))
  );
};

interface SortableFieldRowProps {
  fieldId: string;
  labelRegister: UseFormRegister<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  onDelete: () => void;
}

const SortableFieldRow: React.FC<SortableFieldRowProps> = ({
  fieldId,
  labelRegister,
  errors,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid="expense-custom-field-row"
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#999",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextInput
            label=""
            name={`${fieldId}_label`}
            register={labelRegister}
            errors={errors}
            placeholder="項目名"
            hasSpacing={false}
            inputWidth="100%"
            inputWidthSp="100%"
          />
        </Box>
        <IconButton
          size="small"
          onClick={onDelete}
          title="削除"
          sx={{
            color: colors.text.secondary,
            flexShrink: 0,
            "&:hover": { color: colors.status.error },
          }}
        >
          <ButtonDeleteIcon width={14} height={16} />
        </IconButton>
      </Stack>
    </Box>
  );
};

const ExpenseCustomFormSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const items = useSelector(
    (s: RootState) => s.expenseCustomFields.list.data,
  );
  const loading = useSelector(
    (s: RootState) => s.expenseCustomFields.list.loading,
  );
  const loaded = useSelector(
    (s: RootState) => s.expenseCustomFields.list.loaded,
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => Object.fromEntries(SECTIONS.map((s) => [s.key, false])));
  const [expandedFixed, setExpandedFixed] = useState<Record<string, boolean>>(
    {},
  );
  const [drafts, setDrafts] = useState<Record<string, DraftField>>({});
  const [pendingDeleteFieldIds, setPendingDeleteFieldIds] = useState<string[]>(
    [],
  );
  const [footerDeleteConfirmOpen, setFooterDeleteConfirmOpen] = useState(false);
  const [footerSaving, setFooterSaving] = useState(false);
  const [orderOverride, setOrderOverride] = useState<string[] | null>(null);

  const editForm = useForm<Record<string, unknown>>({ defaultValues: {} });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    void dispatch(fetchExpenseCustomFields());
  }, [dispatch]);

  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, DraftField> = {};
      items.forEach((it) => {
        const existing = prev[it.field_id];
        next[it.field_id] =
          existing && isDraftDirtyFromRecord(existing, it)
            ? { ...existing, dirty: true }
            : fieldToDraft(it);
      });
      Object.values(prev).forEach((d) => {
        if (d.isNew) next[d.field_id] = d;
      });
      return next;
    });
  }, [items]);

  useEffect(() => {
    Object.values(drafts).forEach((d) => {
      const labelKey = `${d.field_id}_label`;
      if (editForm.getValues(labelKey) !== d.label) {
        editForm.setValue(labelKey, d.label);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, drafts]);

  useEffect(() => {
    const sub = editForm.watch((values, info) => {
      const name = info?.name;
      if (!name || !name.endsWith("_label")) return;
      const fieldId = name.slice(0, -"_label".length);
      const v = values[name];
      if (typeof v !== "string") return;
      setDrafts((p) => {
        const cur = p[fieldId];
        if (!cur || cur.label === v) return p;
        const record = items.find((it) => it.field_id === fieldId);
        const next = { ...cur, label: v };
        return {
          ...p,
          [fieldId]: {
            ...next,
            dirty: isDraftDirtyFromRecord(next, record),
          },
        };
      });
    });
    return () => sub.unsubscribe();
  }, [editForm, items]);

  const orderedItems = useMemo(() => {
    const list = [...items];
    if (orderOverride && orderOverride.length > 0) {
      const orderMap = new Map(orderOverride.map((id, idx) => [id, idx]));
      list.sort((a, b) => {
        const ai = orderMap.get(a.field_id);
        const bi = orderMap.get(b.field_id);
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return (a.display_order ?? 999) - (b.display_order ?? 999);
      });
    } else {
      list.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    }
    return list;
  }, [items, orderOverride]);

  // 並び替えは「更新」ボタンとは独立で即時 PATCH するため、hasDirtyDrafts には含めない。
  const hasDirtyDrafts = useMemo(
    () =>
      pendingDeleteFieldIds.length > 0 ||
      Object.values(drafts).some((d) => {
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === d.field_id),
        );
      }),
    [drafts, items, pendingDeleteFieldIds],
  );


  const handleAddCustomField = () => {
    const tempId = `__new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setDrafts((p) => ({
      ...p,
      [tempId]: {
        field_id: tempId,
        label: "",
        field_type: "text",
        options: "",
        dirty: true,
        isNew: true,
      },
    }));
  };

  const handleDeleteFieldClick = (fieldId: string) => {
    const draft = drafts[fieldId];
    if (draft?.isNew) {
      setDrafts((p) => {
        const { [fieldId]: _, ...rest } = p;
        return rest;
      });
      return;
    }
    setPendingDeleteFieldIds((p) => [...p, fieldId]);
  };

  // 並び替え後の挙動 (工事一覧のステータスと同じ方針):
  //   - 即座に orderOverride を更新して UI 反映 (描画はローカル優先)
  //   - BE 既存項目の display_order は background で PATCH (await しない / refetch しない)
  //   - 「更新」ボタンとは独立。hasDirtyDrafts には含めない
  //   - 失敗時のみ toast + refetch で巻き戻し
  //   - ローカル新規項目は drafts 内なので、保存 (persistDraft) 時に override の
  //     位置から display_order を算出する
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const beItems = orderedItems.filter(
      (it) => !pendingDeleteFieldIds.includes(it.field_id),
    );
    const localItems = Object.values(drafts).filter((d) => d.isNew);
    type MergedItem = { field_id: string; isLocal: boolean };
    const merged: MergedItem[] = [
      ...beItems.map((it) => ({ field_id: it.field_id, isLocal: false })),
      ...localItems.map((d) => ({ field_id: d.field_id, isLocal: true })),
    ];
    if (orderOverride && orderOverride.length > 0) {
      const orderMap = new Map(orderOverride.map((id, idx) => [id, idx]));
      merged.sort((a, b) => {
        const ai = orderMap.get(a.field_id);
        const bi = orderMap.get(b.field_id);
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return 0;
      });
    }

    const oldIndex = merged.findIndex((it) => it.field_id === active.id);
    const newIndex = merged.findIndex((it) => it.field_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered: MergedItem[] = arrayMove(merged, oldIndex, newIndex);

    setOrderOverride(reordered.map((r: MergedItem) => r.field_id));

    // ローカル draft (isNew、空/未保存両方) があれば PATCH をスキップ。
    //   - draft の位置が BE 既存項目の display_order スロットを占有した状態で BE 更新
    //     すると、draft が discard / 別位置に動いた時に display_order がずれる。
    //   - 「更新」ボタン押下時の persistOrderChanges で draft 保存後に order をまとめて反映する。
    if (localItems.length > 0) return;

    // BE 既存項目だけ background で PATCH
    const beUpdates: { fieldId: string; newOrder: number }[] = [];
    reordered.forEach((it, idx) => {
      if (it.isLocal) return;
      const be = beItems.find((b) => b.field_id === it.field_id);
      if (!be) return;
      const newOrder = idx + 1;
      if (be.display_order === newOrder) return;
      beUpdates.push({ fieldId: it.field_id, newOrder });
    });
    if (beUpdates.length === 0) return;
    Promise.all(
      beUpdates.map(({ fieldId, newOrder }) =>
        dispatch(
          updateExpenseCustomField({
            field_id: fieldId,
            body: { display_order: newOrder },
          }),
        ).unwrap(),
      ),
    ).catch((e) => {
      showToast({
        type: "error",
        message:
          (e as { message?: string })?.message ||
          "並び替えの保存に失敗しました",
      });
      void dispatch(fetchExpenseCustomFields());
      setOrderOverride(null);
    });
  };

  const persistDraft = async (
    fieldId: string,
    draft: DraftField,
    opts?: { quiet?: boolean },
  ): Promise<boolean> => {
    if (!draft.label.trim()) {
      showToast({ type: "error", message: "項目名は必須です" });
      return false;
    }
    const optionsArr =
      draft.field_type === "select"
        ? draft.options
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined;
    if (
      draft.field_type === "select" &&
      (!optionsArr || optionsArr.length === 0)
    ) {
      showToast({
        type: "error",
        message: "選択フィールドは選択肢が 1 件以上必要です",
      });
      return false;
    }

    if (draft.isNew) {
      const latest = store.getState().expenseCustomFields.list.data;
      // ローカル項目を DnD で並び替えていた場合は override の位置から
      // display_order を算出する。並び替えていなければ末尾に追加。
      const overrideIdx = orderOverride?.indexOf(fieldId) ?? -1;
      const nextOrder =
        overrideIdx >= 0
          ? overrideIdx + 1
          : latest.reduce(
              (m, it) => Math.max(m, it.display_order ?? 0),
              0,
            ) + 1;
      const r = await dispatch(
        createExpenseCustomField({
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
          display_order: nextOrder,
        }),
      );
      if (createExpenseCustomField.fulfilled.match(r)) {
        setDrafts((p) => {
          const { [fieldId]: _, ...rest } = p;
          return rest;
        });
        if (!opts?.quiet) {
          showToast({ type: "success", message: "項目を追加しました" });
        }
        return true;
      }
      showToast({
        type: "error",
        message: (r.payload as string) || "追加に失敗しました",
      });
      return false;
    }

    const r = await dispatch(
      updateExpenseCustomField({
        field_id: fieldId,
        body: {
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
        },
      }),
    );
    if (updateExpenseCustomField.fulfilled.match(r)) {
      setDrafts((p) => ({ ...p, [fieldId]: { ...p[fieldId], dirty: false } }));
      if (!opts?.quiet) {
        showToast({ type: "success", message: "更新しました" });
      }
      return true;
    }
    showToast({
      type: "error",
      message: (r.payload as string) || "更新に失敗しました",
    });
    return false;
  };

  // orderOverride に基づいて BE 既存項目の display_order を一括 PATCH する。
  // (drafts が存在するときに handleDragEnd で skip した分を、save 時にまとめて反映する役割)
  const persistOrderChanges = async (): Promise<boolean> => {
    if (!orderOverride || orderOverride.length === 0) return true;
    const latest = store.getState().expenseCustomFields.list.data;
    for (let idx = 0; idx < orderOverride.length; idx++) {
      const fid = orderOverride[idx];
      const be = latest.find((it) => it.field_id === fid);
      if (!be) continue;
      const targetOrder = idx + 1;
      if ((be.display_order ?? 0) === targetOrder) continue;
      const r = await dispatch(
        updateExpenseCustomField({
          field_id: fid,
          body: { display_order: targetOrder },
        }),
      );
      if (!updateExpenseCustomField.fulfilled.match(r)) {
        showToast({
          type: "error",
          message: (r.payload as string) || "並び替えの保存に失敗しました",
        });
        return false;
      }
    }
    return true;
  };

  // ラベル未入力で空の新規行を drafts から取り除く。
  // BE には届かない (label 必須でエラー) ので、UI 上もリセット時に整理する。
  const dropEmptyNewDrafts = () => {
    setDrafts((p) => {
      const next: typeof p = {};
      for (const [k, v] of Object.entries(p)) {
        if (v.isNew && v.label.trim() === "") continue;
        next[k] = v;
      }
      return next;
    });
  };

  const runFooterSaveOnly = async () => {
    const ids = Object.keys(drafts).filter((id) => {
      const d = drafts[id];
      if (!d) return false;
      if (d.isNew) return d.label.trim().length > 0;
      return isDraftDirtyFromRecord(
        d,
        items.find((it) => it.field_id === id),
      );
    });
    if (ids.length === 0) return;
    setFooterSaving(true);
    try {
      for (const id of ids) {
        const ok = await persistDraft(id, drafts[id], { quiet: true });
        if (!ok) return;
      }
      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;
      setOrderOverride(null);
      dropEmptyNewDrafts();
      await dispatch(fetchExpenseCustomFields());
      showToast({ type: "success", message: "更新しました" });
    } finally {
      setFooterSaving(false);
    }
  };

  const handleFooterUpdateClick = () => {
    if (pendingDeleteFieldIds.length > 0) {
      setFooterDeleteConfirmOpen(true);
      return;
    }
    void runFooterSaveOnly();
  };

  const confirmFooterDeleteAndSave = async () => {
    const toDelete = [...pendingDeleteFieldIds];
    const draftsSnapshot = { ...drafts };
    setFooterDeleteConfirmOpen(false);
    setFooterSaving(true);
    try {
      for (const fid of toDelete) {
        const r = await dispatch(deleteExpenseCustomField(fid));
        if (!deleteExpenseCustomField.fulfilled.match(r)) {
          showToast({
            type: "error",
            message: (r.payload as string) || "削除に失敗しました",
          });
          return;
        }
      }
      setPendingDeleteFieldIds([]);
      await dispatch(fetchExpenseCustomFields());

      const toSaveIds = Object.keys(draftsSnapshot).filter((id) => {
        if (toDelete.includes(id)) return false;
        const d = draftsSnapshot[id];
        if (!d) return false;
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === id),
        );
      });
      for (const id of toSaveIds) {
        const ok = await persistDraft(id, draftsSnapshot[id], { quiet: true });
        if (!ok) return;
      }
      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;
      setOrderOverride(null);
      dropEmptyNewDrafts();
      await dispatch(fetchExpenseCustomFields());
      showToast({ type: "success", message: "更新しました" });
    } finally {
      setFooterSaving(false);
    }
  };

  const cancelFooterDeleteConfirm = () => {
    setFooterDeleteConfirmOpen(false);
  };

  const toggleFixed = (key: string) =>
    setExpandedFixed((p) => ({ ...p, [key]: !p[key] }));

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          mt: 1,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        支出登録フォームに追加する項目を管理します。
        固定項目はシステム既定で編集不可、追加項目はラベルを編集できます。
      </Typography>

      {loading && !loaded ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            width: "100%",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      ) : (
      <Stack spacing={1.5} useFlexGap sx={{ width: { xs: "100%", md: "60%" } }}>
        {SECTIONS.map((section) => {
          const beFields = orderedItems.filter(
            (it) => !pendingDeleteFieldIds.includes(it.field_id),
          );
          const localNewDrafts = Object.values(drafts).filter((d) => d.isNew);
          const customFields: Array<
            Pick<
              ExpenseCustomFieldRecord,
              "field_id" | "label" | "field_type"
            >
          > = [
            ...beFields,
            ...localNewDrafts.map((d) => ({
              field_id: d.field_id,
              label: d.label,
              field_type: d.field_type,
            })),
          ];
          // BE 項目とローカル項目を混在で並び替え可能にするため、
          // override が存在する場合はマージ後リストにも適用する。
          if (orderOverride && orderOverride.length > 0) {
            const orderMap = new Map(
              orderOverride.map((id, idx) => [id, idx]),
            );
            customFields.sort((a, b) => {
              const ai = orderMap.get(a.field_id);
              const bi = orderMap.get(b.field_id);
              if (ai !== undefined && bi !== undefined) return ai - bi;
              if (ai !== undefined) return -1;
              if (bi !== undefined) return 1;
              return 0;
            });
          }
          const expanded = expandedSections[section.key] ?? false;
          return (
            <Accordion
              key={section.key}
              sx={ACCORDION_SX}
              expanded={expanded}
              onChange={(_, next) =>
                setExpandedSections((p) => ({ ...p, [section.key]: next }))
              }
            >
              <AccordionSummary expandIcon={null} sx={ACCORDION_SUMMARY_SX}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: expanded ? "#DBF8FF" : "transparent",
                      color: expanded ? "#0E9DBC" : "#3e3e3e",
                      transition: "all 0.2s",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "#DBF8FF",
                        "& svg": { color: "#0E9DBC" },
                      },
                    }}
                  >
                    <ChevronRightIcon
                      sx={{
                        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                        fontSize: 20,
                        color: "inherit",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0, gap: "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#3E3E3E" }}
                    >
                      {section.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.text.secondary,
                        backgroundColor: "#F5F5F5",
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      {customFields.length}件
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #E0E0E0", p: 2 }}>
                {section.fixedFields.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Box
                      onClick={() => toggleFixed(section.key)}
                      sx={[
                        {
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          width: "fit-content",
                          maxWidth: "100%",
                          borderRadius: "8px",
                          px: 0.5,
                          py: 0.25,
                          transition: "color 0.2s",
                        },
                        expandedFixed[section.key]
                          ? {
                              color: "#0E9DBC",
                              "& svg": { color: "#0E9DBC" },
                            }
                          : {
                              color: "#3E3E3E",
                              "&:hover": {
                                color: "#0E9DBC",
                                "& svg": { color: "#0E9DBC" },
                              },
                            },
                      ]}
                    >
                      {expandedFixed[section.key] ? (
                        <ExpandMoreIcon sx={{ fontSize: 14, color: "inherit" }} />
                      ) : (
                        <ChevronRightIcon
                          sx={{ fontSize: 14, color: "inherit" }}
                        />
                      )}
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 500, color: "inherit" }}
                      >
                        固定項目 ({section.fixedFields.length}件)
                      </Typography>
                    </Box>
                    <Collapse in={!!expandedFixed[section.key]}>
                      <Stack spacing={0.5}>
                        {section.fixedFields.map((f) => (
                          <Box
                            key={f.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.75,
                              fontSize: 12,
                              backgroundColor: "rgba(0,0,0,0.02)",
                              borderRadius: 1,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>{f.label}</Box>
                            {f.required && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#DA384C",
                                  fontWeight: 700,
                                }}
                              >
                                必須
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )}

                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#3E3E3E",
                      mb: 1,
                    }}
                  >
                    追加項目
                  </Typography>
                  {customFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        // BE 既存行 + ローカル新規 (isNew) も並び替え対象に含める。
                        items={customFields.map((cf) => cf.field_id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack spacing={1} useFlexGap sx={{ mb: 1 }}>
                          {customFields.map((f) => {
                            const draft = drafts[f.field_id];
                            if (!draft) return null;
                            return (
                              <SortableFieldRow
                                key={f.field_id}
                                fieldId={f.field_id}
                                labelRegister={editForm.register}
                                errors={editForm.formState.errors}
                                onDelete={() =>
                                  handleDeleteFieldClick(f.field_id)
                                }
                              />
                            );
                          })}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  )}
                  <Can action="config:edit">
                    <CustomButton
                      label="新しい項目を追加"
                      buttonCategory="clearPrimary"
                      onClick={handleAddCustomField}
                      sx={{ width: "100%", borderStyle: "dashed" }}
                    />
                  </Can>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
      )}

      <CustomModal
        openModal={footerDeleteConfirmOpen}
        handleCloseModal={cancelFooterDeleteConfirm}
        title="削除の確認"
        modalType="deleteModal"
        bodyText=""
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: "#666", mb: 2 }}>
            {pendingDeleteFieldIds.length}
            件の項目を削除します。
            <br />
            この操作は取り消せません。削除してよろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={cancelFooterDeleteConfirm}
            buttonCategory="cancelModal"
          />
          <Can action="config:edit">
            <CustomButton
              label="削除する"
              onClick={() => void confirmFooterDeleteAndSave()}
              buttonCategory="deleteModal"
            />
          </Can>
        </Box>
      </CustomModal>

      <FixedActionBar
        submitLabel="更新"
        isLoading={footerSaving}
        disabled={!hasDirtyDrafts || footerSaving}
        onSubmit={() => void handleFooterUpdateClick()}
      />
    </Box>
  );
};

export default ExpenseCustomFormSection;
